import React, { useCallback, useMemo, useState } from "react";
import AddPurchasePanel, { AddPurchasePanelProps } from "../index";

/**
 * 打开加购面板所需的参数类型
 * @description
 * 继承自 `AddPurchasePanelProps`，允许传入所有 `AddPurchasePanel` 的原生属性。
 * 同时，将 `product` 属性设为必填项，因为它是显示面板的核心数据。
 */
export type OpenAddPurchasePanel = AddPurchasePanelProps & {
  product: AddPurchasePanelProps["product"];
};

/**
 * `useAddPurchasePanel` Hook
 * @description
 * 加购弹窗组件，一个用于简化 `AddPurchasePanel` 组件使用的 React Hook。
 * 它封装了面板的显示/隐藏状态管理、属性传递以及回调处理，
 * 允许开发者通过函数调用的方式便捷地打开和关闭加购面板。
 *
 * @example
 * ```tsx
 * const { openPanel, AddPurchasePanelComponent } = useAddPurchasePanel();
 *
 * const handleOpenPanel = () => {
 *   openPanel({
 *     product: currentProduct,
 *     confirm: (params) => console.log('Confirmed:', params),
 *     close: () => console.log('Panel closed'),
 *   });
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleOpenPanel}>Open Panel</Button>
 *     {AddPurchasePanelComponent}
 *   </>
 * );
 * ```
 */
export const useAddPurchasePanel = () => {
  // 使用 state 存储传递给面板的 props，当 props 不为 null 时，面板被认为是“激活”状态
  const [props, setProps] = useState<OpenAddPurchasePanel | null>(null);

  /**
   * 打开加购面板
   * @param panelProps {OpenAddPurchasePanel} 打开加购面板组件AddPurchasePanel所需的 props
   */
  const openPanel = useCallback((panelProps: OpenAddPurchasePanel) => {
    setProps(panelProps);
  }, []);

  /**
   * 关闭加购面板
   */
  const closePanel = useCallback(() => {
    // 如果外界传入了 close 回调，则在关闭时触发它
    props?.close?.();
    setProps(null);
  }, [props]);

  // 使用 useMemo 缓存 AddPurchasePanel 组件实例，避免不必要的重渲染
  const AddPurchasePanelComponent = useMemo(() => {
    if (!props) {
      return null;
    }

    return (
      <AddPurchasePanel
        {...props}
        // 核心改动：优先使用 props 中传入的 visible，若未提供，则默认为 true。
        // 这使得面板在 openPanel 调用后默认可见，同时保留了外部控制其可见性的能力。
        visible={props.visible ?? true}
        // 重写 close 方法，以确保 hook 内部状态能够正确清理
        close={params => {
          // 优先执行外部传入的 close 回调
          props.close?.(params);
          // 然后清理内部状态，隐藏并卸载面板
          setProps(null);
        }}
        // 保持对 confirm 回调的代理
        confirm={params => {
          props.confirm?.(params);
        }}
      />
    );
  }, [props]);

  return {
    openPanel,
    closePanel,
    AddPurchasePanelComponent,
  };
};
